import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

/** Matches native admin filter `<select>` styling (e.g. Reports "All tools"). */
const SELECT_CONTROL_CLASS =
  'rounded-md border border-gray-300 bg-white px-3 py-2 pr-14 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500';

const FILTER_BAR_WIDTH = 'w-56 min-w-56 max-w-56 shrink-0';

const ICON_SLOT_CLASS =
  'absolute top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-gray-500';

const ChevronIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

const ClearIcon = () => (
  <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
  </svg>
);

const SearchableSelect = ({
  value = '',
  selectedLabel = '',
  onChange,
  loadOptions,
  placeholder = 'Select...',
  className = '',
  debounceMs = 300,
  disabled = false,
  id,
  name,
  required = false,
}) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const requestIdRef = useRef(0);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState(selectedLabel || '');
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const widthClass = className || FILTER_BAR_WIDTH;
  const controlClass = `w-full ${SELECT_CONTROL_CLASS}`;

  useBodyScrollLock(open);

  const navigableItems = useMemo(() => {
    const items = [];
    if (!required) {
      items.push({ id: '', name: placeholder, isPlaceholder: true });
    }
    return items.concat(options);
  }, [required, placeholder, options]);

  useEffect(() => {
    if (!value) {
      setLabel('');
      setQuery('');
      setSearchTerm('');
      return;
    }
    if (selectedLabel) setLabel(selectedLabel);
  }, [value, selectedLabel]);

  // Debounce typed queries; empty query applies immediately.
  useEffect(() => {
    if (!open) return;
    if (query === searchTerm) return;
    if (query === '') {
      setSearchTerm('');
      return;
    }
    const timer = setTimeout(() => setSearchTerm(query), debounceMs);
    return () => clearTimeout(timer);
  }, [query, open, debounceMs, searchTerm]);

  const fetchOptions = useCallback(
    async (search) => {
      if (!loadOptions) return;
      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const results = await loadOptions(search || '');
        if (requestId !== requestIdRef.current) return;
        setOptions(Array.isArray(results) ? results : []);
      } catch (error) {
        console.error('Failed to load select options:', error);
        if (requestId === requestIdRef.current) setOptions([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [loadOptions]
  );

  useEffect(() => {
    if (!open) return;
    fetchOptions(searchTerm);
  }, [open, searchTerm, fetchOptions]);

  useEffect(() => {
    if (!open) return;
    setHighlightIndex(navigableItems.length ? 0 : -1);
  }, [open, searchTerm, options, navigableItems.length]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open || highlightIndex < 0 || !listRef.current) return;
    listRef.current.querySelector(`[data-index="${highlightIndex}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, open]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const closeDropdown = () => {
    setOpen(false);
    setHighlightIndex(-1);
  };

  const openDropdown = () => {
    if (disabled) return;
    setSearchTerm(query);
    setHighlightIndex(-1);
    setOpen(true);
  };

  const handleSelect = (option) => {
    const isClear = option?.isPlaceholder;
    setLabel(isClear ? '' : option?.name || '');
    onChange?.(option?.id ?? '', isClear ? null : option);
    if (isClear) {
      setQuery('');
      setSearchTerm('');
    }
    closeDropdown();
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setLabel('');
    setQuery('');
    setSearchTerm('');
    onChange?.('', null);
    closeDropdown();
  };

  const moveHighlight = (delta) => {
    if (!navigableItems.length) return;
    setHighlightIndex((prev) => {
      const next = (prev < 0 ? 0 : prev) + delta;
      if (next < 0) return navigableItems.length - 1;
      if (next >= navigableItems.length) return 0;
      return next;
    });
  };

  const handleKeyDown = (event) => {
    if (!open) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveHighlight(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveHighlight(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (highlightIndex >= 0 && navigableItems[highlightIndex]) {
        handleSelect(navigableItems[highlightIndex]);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closeDropdown();
    }
  };

  const displayText = value ? label || selectedLabel || placeholder : placeholder;
  const showClear = !!value && !required;

  return (
    <div ref={containerRef} className={`relative ${widthClass}`}>
      <input type="hidden" name={name} value={value || ''} required={required} />
      {!open ? (
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={openDropdown}
          className={`${controlClass} flex items-center text-left disabled:cursor-not-allowed disabled:opacity-60 ${
            value ? 'text-gray-800' : 'text-gray-500'
          }`}
        >
          <span className="min-w-0 flex-1 truncate">{displayText}</span>
        </button>
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type to search..."
          disabled={disabled}
          className={`${controlClass} text-gray-800`}
          aria-expanded={open}
          aria-controls={id ? `${id}-listbox` : undefined}
          role="combobox"
          aria-autocomplete="list"
        />
      )}

      {showClear && !open && (
        <button
          type="button"
          aria-label="Clear selection"
          className={`${ICON_SLOT_CLASS} right-7 text-gray-400 hover:text-gray-700`}
          onMouseDown={handleClear}
        >
          <ClearIcon />
        </button>
      )}
      <span className={`${ICON_SLOT_CLASS} pointer-events-none right-2`}>
        <ChevronIcon />
      </span>

      {open && (
        <ul
          ref={listRef}
          id={id ? `${id}-listbox` : undefined}
          role="listbox"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {loading && <li className="px-3 py-2 text-sm text-gray-500">Searching…</li>}
          {!loading && navigableItems.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500">No matches</li>
          )}
          {!loading &&
            navigableItems.map((option, index) => {
              const isHighlighted = index === highlightIndex;
              const isSelected =
                !option.isPlaceholder && value && String(value) === String(option.id);
              return (
                <li
                  key={option.isPlaceholder ? '__placeholder__' : option.id}
                  data-index={index}
                >
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected || isHighlighted}
                    className={`w-full px-3 py-2 text-left text-sm ${
                      option.isPlaceholder ? 'text-gray-500' : 'text-gray-800'
                    } ${
                      isHighlighted || isSelected ? 'bg-indigo-50 text-indigo-900' : 'hover:bg-gray-50'
                    }`}
                    onMouseEnter={() => setHighlightIndex(index)}
                    onClick={() => handleSelect(option)}
                  >
                    {option.name}
                  </button>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
};

export default SearchableSelect;
